import { Component, ErrorInfo, ReactNode } from 'react'

import router from 'next/router'

import { ServerProps } from '@web/hooks/useGlobalStaticState'
import { PHASE } from '@web/libs/constants'
import { extractErrorMessage, neloLogger, NeloLoggerLevel } from '@web/libs/utils'

import ErrorPage from './ErrorPage'

interface Props {
  fallback?: ReactNode
  serverProps?: ServerProps
  children?: ReactNode
  hasErrorFromPage?: boolean
}

interface State {
  hasError: boolean
}

export class KnownError extends Error {
  level: NeloLoggerLevel | 'none'

  /**
   * 알려진 에러
   * @param message 에러 메시지
   * @param level 넬로 로깅 레벨, 'none'은 넬로 로그를 발송하지 않습니다. 기본값: 'error'
   */
  constructor(message: string, level: NeloLoggerLevel | 'none' = 'error') {
    super(message)
    this.level = level
  }
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: props.hasErrorFromPage || false }
  }

  componentDidCatch(error: KnownError, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    if (PHASE === 'development' || PHASE === 'qa') console.error({ error, errorInfo })

    if (error.level === 'none') {
      return
    }

    const errorLog = {
      message: extractErrorMessage(error),
      userAgent: window && window.navigator.userAgent,
      page: window ? location.href : this.props.serverProps?.path,
    }
    const metaData = {
      referer: window ? window.document.referrer : this.props.serverProps?.referrer,
      stack: error.stack,
    }

    neloLogger(error.level ?? 'error', errorLog, metaData)

    this.setState({ hasError: true })
  }

  componentDidUpdate(): void {
    const closeError = () => this.setState({ hasError: false })

    if (this.state.hasError) router.events.on('beforeHistoryChange', closeError)
    else router.events.off('beforeHistoryChange', closeError)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) return this.props.fallback
      return <ErrorPage />
    }

    return this.props.children
  }
}

export default ErrorBoundary
